import { Active, Over, UniqueIdentifier } from '@dnd-kit/core';
import { dequal } from 'dequal';
import produce, { enableMapSet } from 'immer';
import { cloneDeep } from 'lodash-es';
import create from 'zustand';

import { Field, FieldText } from '@components/form/dynamic-fields';
import {
  FieldDefaultProps,
  FieldId,
  FieldType,
  FieldTypeKind,
  FormV2,
  NormalizedFieldType,
  createFieldOfKind,
  fieldDefaultProps,
} from '@components/form/v2';
import { arrayMove } from '@utils/collections';
import { escapeHtml, randomId } from '@utils/strings';
import { ImmerUpdateFn } from '@utils/types';

import { isCompleteCondition } from './condition-helpers';
import { FormBuilderProps } from './form-builder';
import {
  FieldContainingOptions,
  FormBuilderState,
  ProjectedTreePlacement,
  StateField,
  StateFieldMap,
  StateRootIds,
  ViewportType,
} from './state';
import {
  deserializeState,
  duplicateFieldInState,
  isV2Form,
  normalizeFields,
  removeFieldFromList,
} from './utils';

enableMapSet();

const makeDefaultState = (overrides?: Partial<FormBuilderState>) => {
  return {
    selectedFieldId: undefined,
    editingOptionId: undefined,
    mode: 'edit',
    viewport: 'desktop',
    newIds: new Set<FieldId>(),
    removedIds: new Set<FieldId>(),
    modifiedIds: new Set<FieldId>(),
    expandedSections: new Set<FieldId>(),
    isSaving: false,
    ...overrides,
  } satisfies Partial<FormBuilderState>;
};

const makeInitialForm = (): FormV2 => ({
  schema: 'v2',
  fields: [
    createFieldOfKind('heading', { label: 'Form heading' }),
    createFieldOfKind('separator'),
    createFieldOfKind('submit', {
      saveLabel: 'Save',
      hideSaveButton: false,
      builderProps: {
        canDuplicate: false,
        canEditKind: false,
        canRemove: false,
        canEditConditions: false,
        canReorder: false,
      },
    }),
  ],
});

const makeInitialSubmission = (): FormV2 => ({
  schema: 'v2',
  fields: [
    createFieldOfKind('heading', { label: 'Form received' }),
    createFieldOfKind('html', {
      content:
        '<p>Thank you for taking the time to complete this important form. We will contact you if we feel we need more information.</p>\n',
    }),
  ],
});

const migrateToV2 = (fields: Field[]): FormV2 => {
  const labelFromText = (text: FieldText): string => {
    if (typeof text === 'string') {
      return text;
    }

    return text['en'] ?? '';
  };

  const migrateFieldToV2 = (field: Field): FieldType => {
    const isRequired = 'validators' in field && field.validators?.includes('required');
    const id = field.id ?? randomId();

    switch (field.type) {
      case 'formGroup':
      case 'conditional':
        return createFieldOfKind('section', {
          id,
          fields: field.fields.map(migrateFieldToV2),
        });

      case 'repeating':
        return createFieldOfKind('section', {
          id,
          fields: field.template.map(migrateFieldToV2),
        });

      case 'sep':
        return createFieldOfKind('separator', { id });

      case 'dateField':
        return createFieldOfKind('input-date', {
          id,
          defaultValue: field.value,
          label: labelFromText(field.text),
          isRequired,
        });

      case 'dropdown': {
        const options = (field.options ?? []).map((opt) => ({
          label: labelFromText(opt.display),
          value: opt.value,
        }));

        return createFieldOfKind('input-dropdown', {
          id,
          defaultValue: field.value
            ? options.find((opt) => opt.value === field.value)?.value
            : null,
          label: labelFromText(field.text),
          options,
          isRequired,
        });
      }

      case 'file':
      case 'file-multi':
        return createFieldOfKind('input-file', {
          id,
          label: labelFromText(field.text),
          isRequired,
          allowMultiple: field.type === 'file-multi',
        });

      case 'phone':
      case 'formField':
        return createFieldOfKind('input-text-small', {
          id,
          label: labelFromText(field.text),
          isRequired,
        });

      case 'h2':
        return createFieldOfKind('subheading', {
          id,
          label: labelFromText(field.text),
        });

      case 'h3':
        return createFieldOfKind('heading', {
          id,
          label: labelFromText(field.text),
        });

      case 'p':
        return createFieldOfKind('html', {
          id,
          content: `<p>${escapeHtml(labelFromText(field.text))}</p>`,
        });

      case 'list':
        const content = Array.isArray(field.text)
          ? field.text.map((text) => `<li>${escapeHtml(labelFromText(text))}</li>`).join('')
          : `<li>${escapeHtml(labelFromText(field.text))}</li>`;
        return createFieldOfKind('html', {
          id,
          content: `<p><ul>${content}</ul></p>`,
        });

      case 'radioGroup': {
        const options = (field.options ?? []).map((opt) => {
          const val = labelFromText(opt);
          return {
            value: val,
            label: val,
          };
        });

        return createFieldOfKind('input-single-choice', {
          id,
          defaultValue: null,
          isRequired,
          label: labelFromText(field.text),
          options,
        });
      }

      case 'selectMulti': {
        const options = (field.options ?? []).map((opt) => ({
          value: opt.value,
          label: labelFromText(opt.label),
        }));

        return createFieldOfKind('input-multiple-choice', {
          id,
          defaultValue: (field.value ?? []).map((opt) => opt.value),
          isRequired,
          label: labelFromText(field.text),
          options,
        });
      }

      case 'label':
        return createFieldOfKind('html', {
          id,
          content: `<p><strong>${escapeHtml(labelFromText(field.text))}</strong></p>`,
        });

      default:
        // Need this fallback to satisfy TS but we shouldn't run into this
        return createFieldOfKind('separator', { id });
    }
  };

  const usableFields = fields.filter((field) => field.type !== 'derived');

  return {
    schema: 'v2',
    fields: usableFields.map(migrateFieldToV2),
  };
};

type CreateBuilderStoreProps = Pick<
  FormBuilderProps,
  'form' | 'onImageSave' | 'predefinedOptionsLists' | 'onFileSave'
> & {
  formType: FormBuilderState['formType'];
  formVariant: FormBuilderState['formVariant'];
  mode: FormBuilderState['mode'];
};

export const createBuilderStore = ({
  formType,
  form,
  formVariant,
  mode,
  ...rest
}: CreateBuilderStoreProps) => {
  const hiddenPredefinedListIds = new Set(
    (rest.predefinedOptionsLists ?? []).filter((list) => list.isHidden).map((list) => list.value)
  );
  const isNew = form === undefined;
  const shouldMigrate = !isV2Form(form);

  const initialize = (): FormV2 => {
    if (!shouldMigrate) {
      return form;
    }

    if (isNew) {
      if (formType === 'form') {
        return makeInitialForm();
      } else {
        return makeInitialSubmission();
      }
    }

    return migrateToV2(form);
  };

  let initialForm = initialize();

  const { fieldMap, rootIds } = normalizeFields(initialForm.fields);

  // It's OK if the key orders change in the objects since we
  // only care about the values themselves.
  let initialFieldMap = cloneDeep(fieldMap);
  let initialRootIds = rootIds.slice();

  const useStore = create<FormBuilderState>(() => ({
    fieldMap,
    rootIds,
    ...makeDefaultState({
      modifiedIds: shouldMigrate ? new Set(Object.keys(initialFieldMap)) : new Set(),
    }),
    isNew,
    formType,
    formVariant,
    removedImages: [],
    removedFiles: [],
    mode: mode,
  }));

  const resetState = () => {
    useStore.setState({
      fieldMap: initialFieldMap,
      rootIds: initialRootIds,
      formVariant,
      ...makeDefaultState(),
    });
  };

  const update = (fn: ImmerUpdateFn<FormBuilderState>) => useStore.setState(produce(fn));

  const startSaving = () => {
    update((state) => {
      state.isSaving = true;
    });
  };

  const stopSaving = () => {
    update((state) => {
      state.isSaving = false;
    });
  };

  const moveFieldInTree = ({
    activeId,
    overId,
    projected,
  }: {
    activeId: UniqueIdentifier;
    overId: UniqueIdentifier;
    projected: ProjectedTreePlacement;
  }) => {
    if (overId === activeId && !projected?.level) return;

    update((state) => {
      const activeItem = state.fieldMap[activeId];
      const overItem = state.fieldMap[overId];
      const currentFlatFields = flatten(state.rootIds, state.fieldMap, state.expandedSections);
      const activeIndex = currentFlatFields.findIndex((field) => field.id === activeId);
      const overIndex = currentFlatFields.findIndex((field) => field.id === overId);

      activeItem.builder.level = overItem.builder.level + (projected?.level ?? 0);
      activeItem.builder.parentId = projected?.parentId ?? undefined;

      const newFlatFields = arrayMove(currentFlatFields, activeIndex, overIndex);
      const newItems = JSON.parse(JSON.stringify(newFlatFields)) as Array<StateField>;
      const updatedChildren: Record<string, Array<string>> = {};

      newItems.forEach((field) => {
        const parent = field.builder.parentId;
        if (!parent) return;

        if (updatedChildren[parent] === undefined) {
          updatedChildren[parent] = [];
        }
        updatedChildren[parent].push(field.id);
      });

      newItems.forEach((field) => {
        const stateField = state.fieldMap[field.id];
        stateField.builder = field.builder;
        if ('fields' in stateField) {
          stateField.fields = stateField.fields.filter((id) => id !== activeId);
        }
        state.modifiedIds.add(field.id);
      });

      state.rootIds = newItems
        .filter((field) => field.builder.parentId === undefined)
        .map((field) => field.id);
      state.rootIds.forEach((id) => state.modifiedIds.add(id));

      Object.keys(updatedChildren).forEach((id) => {
        const field = state.fieldMap[id];
        if (field.kind === 'section') {
          field.fields = updatedChildren[id];
          state.modifiedIds.add(field.id);
        }
      });
    });
  };

  const insertField = (field: FieldType, over: Over) => {
    if (!over.data.current) return;
    const overId = over.data.current?.field.id;
    const isOverSectionEnd = Boolean(over.data.current?.isSectionEnd);

    update((state) => {
      const overField = state.fieldMap[overId];

      state.fieldMap[field.id] = {
        ...field,
        builder: {
          level: 0,
          parentId: overField.builder.parentId,
        },
      } as StateField;
      const activeField = state.fieldMap[field.id];

      if (isOverSectionEnd) {
        if (overField.kind !== 'section') {
          return;
        }

        activeField.builder.parentId = overId;
        overField.fields.push(field.id);
        state.fieldMap[field.id].builder.level = overField.builder.level + 1;
        state.newIds.add(field.id);
        return;
      }

      let containingFieldsList = state.rootIds;

      if (overField.builder.parentId) {
        const parent = state.fieldMap[overField.builder.parentId];
        if (parent.kind === 'section') {
          state.fieldMap[field.id].builder.level = parent.builder.level + 1;
          containingFieldsList = parent.fields;
        }
      }
      const overIndex = containingFieldsList.indexOf(overId);
      if (overIndex === -1) {
        return;
      }

      containingFieldsList.splice(overIndex, 0, field.id);
      state.newIds.add(field.id);
    });
  };

  const moveFieldIntoSection = (activeId: string, overId: string) => {
    if (activeId === overId) return;
    update((state) => {
      const activeField = state.fieldMap[activeId];
      const overField = state.fieldMap[overId];
      if (overField.kind !== 'section') {
        return;
      }

      if (!activeField.builder.parentId) {
        state.rootIds = state.rootIds.filter((id) => id !== activeId);
      } else {
        const parent = state.fieldMap[activeField.builder.parentId];
        if (parent.kind !== 'section') return;
        parent.fields = parent.fields.filter((field) => field !== activeId);
      }

      // When the section is..
      // Open: we insert at the start of the fields list.
      // Closed: we append to the end of the fields list.
      if (state.expandedSections.has(overField.id)) {
        overField.fields.splice(0, 0, activeId);
      } else {
        overField.fields.push(activeId);
      }
      activeField.builder.parentId = overId;
      activeField.builder.level = overField.builder.level + 1;
    });
  };

  const moveFieldToEnd = (active: Active) => {
    const activeId = active.data.current?.field.id;
    update((state) => {
      const activeField = state.fieldMap[activeId];
      if (activeField.builder.parentId) {
        const parent = state.fieldMap[activeField.builder.parentId];
        if (parent.kind !== 'section') {
          return;
        }
        parent.fields = parent.fields.filter((id) => id !== activeId);
        activeField.builder.parentId = undefined;
      } else {
        const currentIndex = state.rootIds.indexOf(activeId);
        state.rootIds.splice(currentIndex, 1);
      }
      state.rootIds.push(activeId);
    });
  };

  const moveField = (active: Active, over: Over) => {
    if (!active.data.current || !over.data.current) return;
    const activeId = active.data.current?.field.id;
    const overId = over.data.current?.field.id;
    const isOverSectionEnd = Boolean(over.data.current?.isSectionEnd);

    if (activeId === overId) {
      return;
    }

    update((state) => {
      const overField = state.fieldMap[overId];
      const activeField = state.fieldMap[activeId];

      if (activeField.builderProps?.canReorder === false) {
        return;
      }

      if (
        activeField.builder.parentId &&
        activeField.builder.parentId === overField.builder.parentId
      ) {
        // Special case: Moving fields within the same section
        const parent = state.fieldMap[activeField.builder.parentId];
        if (parent.kind !== 'section') {
          return;
        }
        const activeIndex = parent.fields.indexOf(activeId);
        const overIndex = parent.fields.indexOf(overId);
        parent.fields.splice(activeIndex, 1);
        parent.fields.splice(overIndex, 0, activeId);
        return;
      }

      let activeFields = state.rootIds;

      if (activeField.builder.parentId) {
        const parent = state.fieldMap[activeField.builder.parentId];
        if (parent.kind !== 'section') {
          return;
        }
        activeFields = parent.fields;
      }

      const fieldIndex = activeFields.indexOf(activeId);
      if (fieldIndex === -1) {
        return;
      }

      activeFields.splice(fieldIndex, 1);

      if (isOverSectionEnd) {
        if (overField.kind !== 'section') {
          return;
        }

        activeField.builder.parentId = overId;
        activeField.builder.level = overField.builder.level + 1;
        overField.fields.push(activeId);

        return;
      }

      let overFields = state.rootIds;

      activeField.builder.parentId = overField.builder.parentId;
      activeField.builder.level = overField.builder.level;

      if (overField.builder.parentId) {
        const parent = state.fieldMap[overField.builder.parentId];
        if (parent.kind === 'section') {
          activeField.builder.level += 1;
          overFields = parent.fields;
        }
      }
      const overIndex = overFields.indexOf(overId);
      if (overIndex === -1) {
        return;
      }

      overFields.splice(overIndex, 0, activeId);
    });
  };

  // Once saved, we need to be able to 'reset' the diff values.
  const savePoint = (overrideForm?: FormV2) => {
    if (overrideForm) {
      const { fieldMap, rootIds } = normalizeFields(overrideForm.fields);
      initialFieldMap = cloneDeep(fieldMap);
      initialRootIds = rootIds.slice();
      update((state) => {
        state.fieldMap = initialFieldMap;
        state.rootIds = initialRootIds;
      });
    } else {
      const currentState = useStore.getState();

      initialFieldMap = JSON.parse(JSON.stringify(currentState.fieldMap));
      initialRootIds = currentState.rootIds.slice();
    }

    update((state) => {
      state.removedIds = new Set<FieldId>();
      state.newIds = new Set<FieldId>();
      state.modifiedIds = new Set<FieldId>();
      state.removedImages = [];
      state.removedFiles = [];
    });
  };

  const useIsDirty = () => {
    const fieldMap = useStore((state) => state.fieldMap);
    const rootIds = useStore((state) => state.rootIds);
    const newIds = useStore((state) => state.newIds);
    const removedIds = useStore((state) => state.removedIds);
    const modifiedIds = useStore((state) => state.modifiedIds);

    if (!dequal(initialRootIds, rootIds)) return true;
    if (newIds.size > 0 || removedIds.size > 0) return true;
    if (modifiedIds.size === 0) return false;

    // Now we know there are no new fields, no removed fields, and the name is still the same.
    // However, we can't know whether the modified IDs were actually modified,
    // or just temporarily modified and then changed back to the previous value.
    // So we need to verify those IDs have actual changes.

    const maybeDirtyIds = Array.from(modifiedIds.values());
    for (let i = 0; i < maybeDirtyIds.length; i++) {
      const fieldId = maybeDirtyIds[i];

      const oldField = initialFieldMap[fieldId];
      const newField = fieldMap[fieldId];

      if (!dequal(oldField, newField)) return true;
    }

    return false;
  };

  const editViewport = (viewport: ViewportType) => {
    update((state) => {
      state.viewport = viewport;
    });
  };

  const closePreview = () => {
    update((state) => {
      state.mode = 'edit';
    });
  };

  const openPreview = () => {
    update((state) => {
      state.mode = 'preview';
    });
  };

  const changeFieldKind = <K extends FieldTypeKind>(
    id: FieldId,
    newKind: K,
    overrideProps: Partial<FieldDefaultProps<K>>
  ) => {
    update((state) => {
      const field = state.fieldMap[id];
      const newDefaults = fieldDefaultProps[newKind];
      const sourceDescription =
        'description' in field && field.description !== undefined ? field.description : undefined;
      const carriesDescription = 'description' in newDefaults && sourceDescription !== undefined;

      state.fieldMap[id] = {
        ...newDefaults,
        ...{
          isRequired: 'isRequired' in field ? field.isRequired : undefined,
          condition: 'condition' in field ? field.condition : undefined,
          ...(carriesDescription ? { description: sourceDescription } : {}),
        },
        ...overrideProps,
        id,
        builder: field.builder,
        kind: newKind,
      } as StateField;

      if (!state.newIds.has(id)) {
        state.modifiedIds.add(id);
      }
    });
  };

  const createChangeHandler =
    <Field extends NormalizedFieldType, Prop extends keyof Field, Value extends Field[Prop]>(
      field: Field,
      property: Prop
    ) =>
    (value: Value) => {
      update((state) => {
        const stateField = state.fieldMap[field.id];
        if (!stateField) return;

        (stateField as unknown as Field)[property] = value;

        if (!state.newIds.has(stateField.id)) {
          state.modifiedIds.add(stateField.id);
        }
      });
    };

  const selectField = (id: FieldId) => {
    expandFieldPath(id);
    update((state) => {
      state.selectedFieldId = id;
    });
  };

  const duplicateField = (fieldId: FieldId) => {
    update((state) => {
      const newId = duplicateFieldInState(fieldId, state, hiddenPredefinedListIds);
      state.newIds.add(newId);
    });
  };

  const deserialize = (): FormV2['fields'] => {
    return deserializeState(useStore.getState());
  };

  const unselect = () => {
    update((state) => {
      state.selectedFieldId = undefined;
      state.editingOptionId = undefined;
    });
  };

  const removeField = (fieldId: FieldId) => {
    update((state) => {
      if (state.selectedFieldId === fieldId) {
        state.selectedFieldId = undefined;
      }

      removeFieldFromList(fieldId, state);

      if (!state.newIds.has(fieldId)) {
        // We don't want to add this field to the removedIds set
        // if it was an unsaved field.
        state.removedIds.add(fieldId);
      }
      state.modifiedIds.delete(fieldId);
      state.newIds.delete(fieldId);
    });
  };

  const addField = <K extends FieldTypeKind>(
    kind: K,
    overrideProps?: Partial<FieldDefaultProps<K> & { id?: FieldId }>
  ) => {
    update((state) => {
      const field = createFieldOfKind(kind, overrideProps);
      const submitButton = Object.keys(state.fieldMap).find(
        (key) => state.fieldMap[key].kind === 'submit'
      );

      const insertIndex = submitButton ? state.rootIds.indexOf(submitButton) : state.rootIds.length;
      state.rootIds.splice(insertIndex, 0, field.id);
      state.fieldMap[field.id] = {
        ...field,
        builder: {
          level: 0,
        },
      } as StateField;

      state.selectedFieldId = field.id;
      state.newIds.add(field.id);
    });
  };

  const expandFieldPath = (id: FieldId) => {
    update((state) => {
      const open = (id: FieldId) => {
        const field = state.fieldMap[id];
        if (field.kind === 'section') {
          state.expandedSections.add(id);
        }

        if (field.builder.parentId) {
          open(field.builder.parentId);
        }
      };

      open(id);
    });
  };

  const collapseFieldPath = (id: FieldId) => {
    update((state) => {
      const close = (id: FieldId) => {
        const field = state.fieldMap[id];
        if (field.kind !== 'section') return;
        state.expandedSections.delete(id);
        field.fields.forEach((childId) => close(childId));
      };

      close(id);
    });
  };

  const toggleExpansion = (id: FieldId) => {
    if (useStore.getState().expandedSections.has(id)) {
      collapseFieldPath(id);
    } else {
      expandFieldPath(id);
    }
  };

  const useFormFieldFromId = <T extends StateField>(id: FieldId): T => {
    return useStore((state) => state.fieldMap[id]) as T;
  };

  const useFormFieldKind = (id: FieldId) => {
    return useStore((state) => {
      return state.fieldMap[id].kind;
    });
  };

  const useSelectedField = () => {
    return useStore((state) =>
      state.selectedFieldId ? state.fieldMap[state.selectedFieldId] : null
    );
  };

  const useHasIncompleteConditions = () => {
    return useStore((state) => {
      for (const fieldId in state.fieldMap) {
        const field = state.fieldMap[fieldId];
        if (!('condition' in field)) continue;
        for (const condition of field.condition.conditions) {
          if (!isCompleteCondition(condition)) return true;
        }
      }
      return false;
    });
  };

  const useIsFieldConditional = (fieldId: FieldId) => {
    return useStore((state) => {
      const field = state.fieldMap[fieldId];
      return 'condition' in field && field.condition.conditions.length > 0;
    });
  };

  const useIsFieldSelected = (fieldId: FieldId) => {
    return useStore((state) => {
      if (!state.selectedFieldId) return false;
      return state.selectedFieldId === fieldId;
    });
  };

  const updateFieldOptions = (
    fieldId: FieldId,
    callback: (field: FieldContainingOptions) => void
  ) => {
    const field = useStore.getState().fieldMap[fieldId];
    if (!field || !('options' in field)) return;

    update((state) => {
      callback(state.fieldMap[fieldId] as FieldContainingOptions);

      if (!state.newIds.has(fieldId)) {
        state.modifiedIds.add(fieldId);
      }
    });
  };

  const flatten = (
    rootIds: StateRootIds,
    fieldMap: StateFieldMap,
    expanded: Set<string>,
    includeCollapsed = false
  ) => {
    let ids: Array<StateField> = [];

    const addIdToList = (id: string) => {
      ids.push(fieldMap[id]);

      const field = fieldMap[id];
      if (field.kind === 'section' && (includeCollapsed || expanded.has(id))) {
        field.fields.forEach(addIdToList);
      }
    };

    rootIds.forEach((rootId) => {
      addIdToList(rootId);
    });

    return ids;
  };

  const useFlattenedList = (includeCollapsed = false) => {
    return useStore((state) => {
      return flatten(state.rootIds, state.fieldMap, state.expandedSections, includeCollapsed);
    });
  };

  const useEditingOption = () => useStore((state) => state.editingOptionId ?? null);
  const useFormRootIds = () => useStore((state) => state.rootIds);
  const useFieldsFromIds = (ids: Array<FieldId>) => {
    return useStore((state) => {
      return ids.map((id) => state.fieldMap[id]);
    });
  };
  const useFormRootFields = () => {
    return useStore((state) => {
      return state.rootIds.map((id) => {
        return state.fieldMap[id];
      });
    });
  };
  const useViewport = () => useStore((state) => state.viewport);
  const useIsEditMode = () => useStore((state) => state.mode === 'edit');
  const useIsSaving = () => useStore((state) => state.isSaving);
  const useIsExpanded = (id: FieldId) => useStore((state) => state.expandedSections.has(id));
  const useExpandedIds = () => useStore((state) => Array.from(state.expandedSections.keys()));

  const useIsNewAndUntouched = () => {
    return useStore((state) => {
      return state.isNew && state.newIds.size === 0;
    });
  };

  const useFormType = () => {
    return useStore((state) => {
      return state.formType;
    });
  };

  const useFormVariant = () => {
    return useStore((state) => {
      return state.formVariant;
    });
  };

  return {
    actions: {
      addField,
      changeFieldKind,
      closePreview,
      collapseFieldPath,
      deserialize,
      duplicateField,
      createChangeHandler,
      editViewport,
      moveField,
      moveFieldToEnd,
      moveFieldInTree,
      openPreview,
      removeField,
      resetState,
      savePoint,
      selectField,
      startSaving,
      stopSaving,
      toggleExpansion,
      unselect,
      updateFieldOptions,
      insertField,
      moveFieldIntoSection,
    },
    getState: useStore.getState,
    useEditingOption,
    useFlattenedList,
    useFormRootIds,
    useFormRootFields,
    useFormFieldKind,
    useFieldsFromIds,
    useIsDirty,
    useIsEditMode,
    useIsSaving,
    useSelectedField,
    useIsFieldSelected,
    useViewport,
    useIsExpanded,
    useExpandedIds,
    useIsNewAndUntouched,
    useFormType,
    useFormFieldFromId,
    useIsFieldConditional,
    useHasIncompleteConditions,
    useFormVariant,
    ...rest,
  };
};

export type CreateFormBuilderStoreType = ReturnType<typeof createBuilderStore>;
